import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { IconQrcode } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../../interfaces/Order';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { toPng } from 'html-to-image';
import { getOrderCodeImageBase64 } from '../../utils/ticketHelper';
import download from 'downloadjs';
import { buildOrderFolio } from '../../utils/ordersUtils';

type Props = {
  order: IOrder;
};

export async function downloadOrderCodeAsPNG(order: IOrder) {
  if (!order?._id || !order?.sequence) {
    console.error('No se pudo generar el código: pedido inválido');
    return;
  }

  const qrDataUrl = await getOrderCodeImageBase64({
    code: order._id,
    format: 'qr',
    options: { width: 150, margin: 1 }
  });

  // Crear nodo contenedor con estilos en línea
  const container = document.createElement('div');

  container.style.width = 'auto';
  container.id = 'temp-ticket-node';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';
  container.style.position = 'absolute';
  container.style.border = '1px solid #ccc';
  container.style.textAlign = 'center';
  container.style.left = '-999999'; // Keep it off-screen
  container.style.top = '0';
  container.style.height = 'auto'; // Allow content to size dynamically
  container.style.padding = '15px'; // Allow content to size dynamically
  container.style.zIndex = '-9999999'; // Keep it out of view
  container.style.overflow = 'visible'; // Ensure no scrollbars are added
  container.style.backgroundColor = 'white'; // ticket background color

  // Crear imagen del QR
  const img = document.createElement('img');
  img.src = qrDataUrl;
  img.alt = 'QR Pedido';
  img.width = 150;
  img.height = 150;

  // Texto debajo
  const label = document.createElement('p');
  label.textContent = `Pedido: ${buildOrderFolio(order?.folioPrefix, order?.sequence)}`;
  label.style.marginTop = '8px';
  label.style.fontSize = '12px';

  // Montar
  container.appendChild(img);
  container.appendChild(label);
  document.body.appendChild(container);

  await new Promise((resolve) => {
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    // fallback por si onload nunca se dispara
    setTimeout(() => resolve(true), 300);
  });
  await new Promise((resolve) => requestAnimationFrame(resolve));

  try {
    const dataUrl = await toPng(container);
    download(dataUrl, `${buildOrderFolio(order?.folioPrefix, order?.sequence)}_codigo.png`);
  } catch (err) {
    console.error('Error al generar imagen del QR:', err);
  } finally {
    document.body.removeChild(container);
  }
}

export function DownloadOrderCodeActionIcon({ order }: Props) {
  const { t } = useTranslation(['CommonButtons']);

  const onDownloadOrderCode = async (order: IOrder) => {
    try {
      await downloadOrderCodeAsPNG(order);
    } catch (e) {
      console.log('[ERROR] Error al descargar el código del pedido', e);
    }
  };

  const handleClick = () => {
    ConfirmationModal({
      title: t('CommonButtons:downloadOrderCode.title'),
      children: <Text>{t('CommonButtons:downloadOrderCode.description')}</Text>,
      onConfirm: () => onDownloadOrderCode(order),
      onCancel: () => {}
    });
  };

  return (
    <Tooltip withinPortal={false} label={t('CommonButtons:downloadOrderCode.title')}>
      <ActionIcon
        onClick={handleClick}
        variant="transparent"
        aria-label={t('CommonButtons:downloadOrderCode.title')}>
        <IconQrcode />
      </ActionIcon>
    </Tooltip>
  );
}
